/**
 * The phone's own compass, read the way a hand-bearing compass is used.
 *
 * WHY THIS IS HERE AND NOT ON THE BOAT SYMBOL. A phone magnetometer measures
 * the PHONE's heading, and on a boat that is usually the wrong number: unless
 * the phone is clamped fore and aft it swings with your body, not the bow, and
 * a plausible wrong heading gets believed in a way that "pointing north"
 * never does. Boats are also poor places for a magnetometer, with an engine,
 * steel and speakers all close by, which is exactly why a hand-bearing compass
 * is held up and away from everything.
 *
 * But "point the phone at the thing and take a bearing" is precisely what a
 * hand-bearing compass IS, and there the phone measuring its own heading stops
 * being a flaw and becomes the whole point. The user is holding it deliberately,
 * aiming it, and reading one number at one moment. That is the use this module
 * serves: taking a bearing to where the anchor went down, so a rode length and
 * a bearing put the anchor on the chart (fb-msca16x7-1).
 *
 * The reading is MAGNETIC. Callers apply variation to get true; the bearing
 * display store already knows the variation.
 *
 * Two platforms, two APIs:
 *   - iOS gives `webkitCompassHeading` on the `deviceorientation` event, and
 *     only after DeviceOrientationEvent.requestPermission() is granted from a
 *     real user gesture.
 *   - Android fires `deviceorientationabsolute`, where `alpha` is measured
 *     anticlockwise from east-ish north, so the heading is 360 - alpha.
 */

export type CompassSupport = 'ios' | 'absolute' | 'none';

/** A single compass reading. */
export interface CompassReading {
  /** Magnetic heading of the phone's top edge, degrees. */
  magneticDeg: number;
  /** The platform's own accuracy figure in degrees, when it offers one. */
  accuracyDeg: number | null;
}

interface IosOrientationEvent extends DeviceOrientationEvent {
  webkitCompassHeading?: number;
  webkitCompassAccuracy?: number;
}

type PermissionCapable = {
  requestPermission?: () => Promise<'granted' | 'denied' | 'default'>;
};

/**
 * The bits of `window` we actually look at. Typed loosely on purpose: the
 * platform-specific properties here are not in the DOM lib, and the point of
 * taking a window at all is that tests can hand in a plain object.
 */
export type CompassWindow = {
  DeviceOrientationEvent?: unknown;
  ondeviceorientationabsolute?: unknown;
};

/** Which compass API, if any, this device offers. Pure enough to stub in tests. */
export function compassSupport(win: CompassWindow = (globalThis.window ?? {}) as CompassWindow): CompassSupport {
  if (typeof win.DeviceOrientationEvent === 'undefined') return 'none';
  const dev = win.DeviceOrientationEvent as PermissionCapable | undefined;
  if (typeof dev?.requestPermission === 'function') return 'ios';
  if (typeof win.ondeviceorientationabsolute !== 'undefined') return 'absolute';
  // Some Android WebViews expose neither flag but still fire the absolute event.
  // Treat DeviceOrientationEvent alone as worth trying rather than refusing.
  return 'absolute';
}

/**
 * Turn one orientation event into a magnetic heading, or null when it carries
 * nothing usable.
 *
 * `alpha` from `deviceorientationabsolute` runs ANTICLOCKWISE from north, so a
 * compass heading is 360 - alpha. Getting that backwards is the classic bug
 * here, and it is silent: bearings come out mirrored about north, so due east
 * reads as due west and nothing looks obviously broken.
 */
export function headingFromEvent(e: DeviceOrientationEvent): CompassReading | null {
  const ios = e as IosOrientationEvent;
  if (typeof ios.webkitCompassHeading === 'number' && Number.isFinite(ios.webkitCompassHeading)) {
    return {
      magneticDeg: norm(ios.webkitCompassHeading),
      accuracyDeg:
        typeof ios.webkitCompassAccuracy === 'number' && ios.webkitCompassAccuracy >= 0
          ? ios.webkitCompassAccuracy
          : null,
    };
  }
  // Only an ABSOLUTE reading means anything: a relative one is measured from
  // wherever the phone happened to be when the page loaded, which is not a
  // compass at all.
  if (e.absolute !== true) return null;
  if (typeof e.alpha !== 'number' || !Number.isFinite(e.alpha)) return null;
  return { magneticDeg: norm(360 - e.alpha), accuracyDeg: null };
}

/** Ask for permission where the platform demands it. True when we may listen. */
export async function requestCompassPermission(
  win: CompassWindow = (globalThis.window ?? {}) as CompassWindow,
): Promise<boolean> {
  const dev = win.DeviceOrientationEvent as PermissionCapable | undefined;
  if (typeof dev?.requestPermission !== 'function') return true; // nothing to ask
  try {
    // MUST be called from a user gesture on iOS, or it rejects.
    return (await dev.requestPermission()) === 'granted';
  } catch {
    return false;
  }
}

/**
 * Listen for compass readings until the returned function is called. Resolves
 * nothing itself: the caller decides what to do with each reading, because
 * taking a bearing means watching the number settle and then committing it.
 */
export function watchCompass(
  onReading: (r: CompassReading) => void,
  win: Pick<Window, 'addEventListener' | 'removeEventListener'> = globalThis.window as Window,
): () => void {
  const handler = (e: Event) => {
    const r = headingFromEvent(e as DeviceOrientationEvent);
    if (r) onReading(r);
  };
  // Both are registered: a device that fires only one simply never sends the
  // other, and a device that fires both gives us the absolute one either way
  // because headingFromEvent rejects a relative reading.
  win.addEventListener('deviceorientationabsolute', handler);
  win.addEventListener('deviceorientation', handler);
  return () => {
    win.removeEventListener('deviceorientationabsolute', handler);
    win.removeEventListener('deviceorientation', handler);
  };
}

/** Degrees into 0..360. */
function norm(deg: number): number {
  return ((deg % 360) + 360) % 360;
}
